import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import SeoHead from "../components/SeoHead";
import ReadingProgressBar from "../components/ReadingProgressBar";
import { FAQS, FAQ_CATEGORIES, buildFaqJsonLd } from "../lib/faqData";

/**
 * /faq — index page listing every FAQ grouped by category. Includes a simple
 * substring filter, a FAQPage JSON-LD blob, and proper meta tags. Stays
 * mobile-first; no animations beyond the existing Tailwind transitions.
 */
function FaqIndexJsonLd() {
  useEffect(() => {
    const id = "faq-jsonld-index";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(buildFaqJsonLd(FAQS));
    return () => {
      const t = document.getElementById(id);
      if (t && t.parentNode) t.parentNode.removeChild(t);
    };
  }, []);
  return null;
}

export default function FaqIndex() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQS;
    return FAQS.filter((f) => {
      const hay = `${f.question} ${(f.answer || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query]);

  const groups = useMemo(() => {
    const map = new Map(FAQ_CATEGORIES.map((c) => [c.id, []]));
    filtered.forEach((f) => {
      if (!map.has(f.category)) map.set(f.category, []);
      map.get(f.category).push(f);
    });
    return FAQ_CATEGORIES.map((c) => ({
      ...c,
      items: map.get(c.id) || [],
    })).filter((g) => g.items.length > 0);
  }, [filtered]);

  return (
    <main
      className="bg-cream-50 min-h-[80vh]"
      data-testid="faq-index-page"
    >
      <ReadingProgressBar />
      <SeoHead
        title="BlendSafe FAQ — Skincare safety with medications"
        description="Common questions about BlendSafe, how verdicts work, supported medications, data sources, and Founding Access."
        canonicalPath="/faq"
      />
      <FaqIndexJsonLd />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <p className="text-[10.5px] uppercase tracking-[0.28em] text-ink-mute font-semibold">
          BlendSafe FAQ
        </p>
        <h1
          data-testid="faq-index-headline"
          className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.025em] text-ink mt-2.5 leading-[1.08]"
        >
          Questions, answered.
        </h1>
        <p className="text-[14.5px] sm:text-[15px] text-ink-soft/85 mt-3 leading-relaxed max-w-md">
          Everything about how BlendSafe checks skincare against medications,
          what each verdict means, and how Founding Access works.
        </p>

        <div className="mt-6 sm:mt-8 relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-light pointer-events-none" />
          <input
            type="text"
            data-testid="faq-index-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions…"
            className="w-full h-12 pl-11 pr-4 rounded-2xl bg-white border border-surface-line text-[16px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-emerald-50 focus:border-emerald-200 transition-all"
          />
        </div>

        {groups.length === 0 ? (
          <p
            data-testid="faq-index-empty"
            className="mt-10 text-[14px] text-ink-mute"
          >
            No questions match “{query}”. Try a different search.
          </p>
        ) : (
          <div className="mt-10 space-y-10">
            {groups.map((g) => (
              <section
                key={g.id}
                data-testid={`faq-index-section-${g.id}`}
              >
                <h2 className="text-[11px] uppercase tracking-[0.28em] font-bold text-ink-mute">
                  {g.label}
                </h2>
                <ul className="mt-4 divide-y divide-surface-line rounded-2xl bg-white border border-surface-line overflow-hidden">
                  {g.items.map((f) => (
                    <li key={f.slug}>
                      <Link
                        to={`/faq/${f.slug}`}
                        data-testid={`faq-index-link-${f.slug}`}
                        className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-cream-50 transition-colors"
                      >
                        <span className="text-[15px] sm:text-[15.5px] font-semibold text-ink leading-snug">
                          {f.question}
                        </span>
                        <ArrowRight className="w-4 h-4 text-ink-light shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        <div className="mt-12 sm:mt-14">
          <Link
            to="/check"
            data-testid="faq-index-cta-check"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.01] active:scale-[0.99]"
          >
            Check your routine free
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </main>
  );
}
